<script>
// layout
import Authenticated from "@/Layouts/Authenticated";
// components
import SupplierTabs from './SupplierTabs.vue';
import Pagination from "@/Components/Pagination";
import SearchFilter from "@/Components/SearchFilter";
// vue packages
import moment from "moment/moment";
import pickBy from "lodash/pickBy";
import debounce from "lodash/debounce";
import mapValues from 'lodash/mapValues'
import {DownloadIcon} from "@heroicons/vue/outline";
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";

export default {
    layout: Authenticated,
    components: {
        Head,
        Link,
        Pagination,
        SearchFilter,
        DownloadIcon,
        SupplierTabs,
    },
    props: {
        organisation: Object,
        commissionInvoices: Array,
        filters: Object,
    },
    created() {
      this.moment = moment;
    },
    data() {
        return {
            form: useForm({
                search: null,
                filter: {
                    starts_at: this.filters.starts_at,
                    ends_at: this.filters.ends_at,
                    date: this.filters.date
                }
            })
        }
    },
    methods: {
        reset() {
            this.form.search = null;
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('frontend.organisations.suppliers.organisation.commission-invoices.show' , this.organisation.id), pickBy(this.form), { preserveState: true, preserveScroll: true });
            }, 500),
        },
    },
}
</script>
<template>
    <Head :title="organisation.name"/>
    <ValidationErrors :show="showErrors"/>
    <div class="w-full space-y-3 mb-6">
        <div>
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                <span
                    v-if="organisation.name === null"
                    v-html="'Create New ' + organisation.type.charAt(0).toUpperCase() + organisation.type.substring(1)">
                </span>
                <span v-else v-html="organisation.name"></span>
            </h1>
            <h2 v-if="organisation.registered_legal_name === null"
                class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="''"></h2>
            <h2 v-else class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="organisation.registered_legal_name"></h2>
        </div>
        <div class="space-y-6 mt-2 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <SupplierTabs v-if="organisation" :organisation="organisation"/>
                <div class="shadow sm:rounded-md sm:overflow-hidden">
                    <div class="bg-white px-4 p-6 space-y-6">
                        <div id="supplier_form" class="space-y-6">
                            <div class="relative">
                                <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div class="w-full border-t border-gray-300"/>
                                </div>
                                <div class="relative flex items-center justify-between">
                                    <span class="pr-3 bg-white text-lg font-bold text-gray-900"> Commission Invoices </span>
                                    <a :href="route('frontend.organisations.suppliers.organisation.commission-invoices.export', {
                                            organisation: organisation.id,
                                            starts_at: form.filter.starts_at ?? null,
                                            ends_at: form.filter.ends_at ?? null,
                                            date: form.filter.date ?? null,
                                            search: form.search ?? null
                                        })"
                                        class="
                                            inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300
                                            text-sm leading-5 font-medium rounded-full text-gray-700 bg-white
                                            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                                        "
                                    >
                                        <DownloadIcon class="-ml-0.5 mr-2 h-4 w-4" />
                                        Export All
                                    </a>
                                </div>
                            </div>
                            <div class="w-full flex gap-3 justify-between">
                                <div class="flex gap-1 w-full items-center">
                                    <div class="flex items-center gap-2 w-full">
                                        <input v-model="form.filter.starts_at" type="date"
                                            name="starts_at" id="starts_at"
                                            autocomplete="starts_at"
                                            class="flex-grow-1 mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="flex items-center gap-2 w-full">
                                        <input v-model="form.filter.ends_at" type="date"
                                            name="ends_at" id="ends_at"
                                            autocomplete="ends_at"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="flex items-center gap-2 w-full">
                                        <select v-model="form.filter.date" id="date" class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value="invoiced_at">Date Raised</option>
                                            <option value="fixture_date">Fixture Date</option>
                                        </select>
                                    </div>
                                    <div class="flex items-center gap-2 w-full">
                                        <search-filter :show-filter="false" v-model:value="form.search" class="w-full" @reset="reset"></search-filter>
                                    </div>
                                </div>
                            </div>
                            <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
                                <div class="flex flex-col mt-3 space-y-3">
                                    <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                            <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                                <table class="min-w-full divide-y divide-gray-200">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Reference</th>
                                                            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Name</th>
                                                            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Invoice Reference</th>
                                                            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date Raised</th>
                                                            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date Due</th>
                                                            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Fixture Date</th>
                                                            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody class="bg-white">
                                                        <tr v-if="commissionInvoices.data.length > 0" v-for="invoice in commissionInvoices.data" :key="invoice.id" class="even:bg-gray-50">
                                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">{{ invoice?.fixture?.reference }}</td>
                                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ invoice?.fixture?.title }}</td>
                                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ invoice?.reference }}</td>
                                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span>{{ moment(invoice.invoiced_at, 'DD-MM-YYYY').format('DD MMM YYYY') }}</span>
                                                            </td>
                                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span>{{ moment(invoice.due_at, 'DD-MM-YYYY').format('DD MMM YYYY') }}</span>
                                                            </td>
                                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span>{{ moment(invoice.fixture.starts_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}</span>
                                                            </td>
                                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span v-if="invoice.fixture?.currency?.code">
                                                                    {{ formatCurrency(invoice.amount, invoice?.fixture?.currency?.code, true) }}
                                                                </span>
                                                                <span v-else>
                                                                    {{ invoice.amount }}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        <tr v-else>
                                                            <td colspan="6" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                There are no invoices available for your search criteria.
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    <Pagination :links="commissionInvoices.links"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>
